/// The canonical RYA sailing mnemonics every student should have at
/// their fingertips. Two shapes share one type:
///
///   1. **Acronym mnemonics** (CADET, WOBBLE, Five Essentials,
///      MAYDAY …) — each letter or number has its own step / check.
///   2. **Rule mnemonics** (Rule of Twelfths, Buys Ballot's Law) —
///      one short rule with a worked example, no per-letter
///      breakdown.
///
/// Some entries here duplicate data already in domain packages
/// (CADET in compass, WOBBLE in sailing). That's deliberate — the
/// mnemonics module is the unified reference / drill catalogue and
/// needs all of them in one place. The domain packages remain the
/// source of truth for any computation; this package is for
/// learning.

export type MnemonicDomain =
  | "compass"
  | "engine"
  | "sailing"
  | "tides"
  | "weather"
  | "irpcs"
  | "passage"
  | "distress";

export type CourseTier =
  | "dinghy-l1"
  | "dinghy-l2"
  | "dinghy-l3"
  | "power-l2"
  | "power-int"
  | "competent-crew"
  | "day-skipper"
  | "coastal-skipper"
  | "yachtmaster-offshore"
  | "yachtmaster-ocean";

export interface MnemonicStep {
  /// Letter, number, or position — what the student writes in the
  /// vertical column when revising.
  key: string;
  /// Short label for the step (e.g. "Water", "Course made good").
  label: string;
  /// Full explanation — what to check / what to do / what it means.
  detail: string;
}

export interface Mnemonic {
  /// Stable identifier — used by the drill game.
  id: string;
  /// The mnemonic as the student writes it: "CADET", "WOBBLE",
  /// "MAYDAY", "5 Essentials", "Rule of Twelfths".
  acronym: string;
  /// Full title: "Compass error sign convention", "Pre-engine
  /// daily checks".
  name: string;
  domain: MnemonicDomain;
  /// One-line summary of what the mnemonic captures.
  summary: string;
  /// When in a sailing/powerboat day you reach for this. Used in
  /// the drill's reveal panel and in the "which mnemonic for this
  /// scenario?" mode.
  whenToUse: string;
  /// Step-by-step expansion for acronym mnemonics. Empty for
  /// rule-shaped entries.
  steps: readonly MnemonicStep[];
  /// Body text for rule-shaped mnemonics (Rule of Twelfths, Buys
  /// Ballot's Law). null for acronym mnemonics.
  rule: string | null;
  /// Worked example or scenario the student can pattern-match
  /// against. null if there's nothing to work.
  example: string | null;
  /// Course tiers where this is taught. Drives course-filter views.
  courses: readonly CourseTier[];
  /// Three or more example scenarios the drill picks from when
  /// asking "which mnemonic for this situation?".
  scenarios: readonly string[];
  /// Truthiness flag: this mnemonic is the de-facto RYA standard
  /// (CADET, Five Essentials, Rule of Twelfths). When false the
  /// mnemonic is widely taught but not strictly RYA-canonical —
  /// shown in the panel with a "common variation" tag.
  canonical: boolean;
}

export const MNEMONICS: readonly Mnemonic[] = Object.freeze([
  {
    id: "cadet",
    acronym: "CADET",
    name: "Compass error sign convention",
    domain: "compass",
    summary:
      "Compass → True: add East, subtract West. True → Compass: reverse.",
    whenToUse:
      "Every time you convert between a compass course (what you steer) and a true course (what you plot). Built into the chartwork compass calculator.",
    steps: [
      {
        key: "C",
        label: "Compass",
        detail: "The course read off the steering compass — the value you actually steer.",
      },
      {
        key: "A",
        label: "ADd",
        detail: "ADd East — easterly errors are added when going from compass to true.",
      },
      {
        key: "D",
        label: "Deviation",
        detail: "Boat's own magnetic error — varies with heading, read from the deviation card.",
      },
      {
        key: "E",
        label: "East",
        detail: "Easterly errors get added; westerly errors get subtracted (going C → T).",
      },
      {
        key: "T",
        label: "True",
        detail: "True course — the one you plot on the chart.",
      },
    ],
    rule: null,
    example:
      "Compass 045°, Deviation 2°E, Variation 4°W → Magnetic = 045° + 2°E = 047°; True = 047° − 4°W = 043°.",
    courses: ["day-skipper", "coastal-skipper", "yachtmaster-offshore"],
    scenarios: [
      "You steer 045° by compass; the deviation card reads 2°E for that heading; chart variation is 4°W. What's the true course?",
      "You want to steer 270° true; variation is 5°E, deviation is 3°W. What compass course?",
      "The DSC shows you've drifted off course by 6°; the wind veered. Which conversion gets you back?",
    ],
    canonical: true,
  },

  {
    id: "wobble",
    acronym: "WOBBLE",
    name: "Pre-engine daily checks",
    domain: "engine",
    summary:
      "Water, Oil, Belts, Bilges, Leaks, Exhaust — two minutes round the engine before you turn the key.",
    whenToUse:
      "Every day before the first engine start. Catches almost every common engine-failure root cause.",
    steps: [
      { key: "W", label: "Water", detail: "Coolant header tank within marks; raw-water sea-cock open + strainer clear." },
      { key: "O", label: "Oil", detail: "Engine + gearbox oil on the dipstick within marks." },
      { key: "B", label: "Belts", detail: "Alternator and water-pump belts taut, no fraying or polished glazing." },
      { key: "B", label: "Bilges", detail: "Bilge dry under the engine; an oil slick or rising water = a leak." },
      { key: "L", label: "Leaks", detail: "Fuel lines, raw-water hoses, exhaust elbow, coolant hoses — any drip or fresh stain." },
      { key: "E", label: "Exhaust", detail: "Water flowing out of the exhaust within 30 seconds of starting." },
    ],
    rule: null,
    example:
      "You see a slick of clean water under the engine that wasn't there yesterday → Bilges flagged → trace the leak before turning the key.",
    courses: ["competent-crew", "day-skipper", "power-l2", "power-int"],
    scenarios: [
      "You're about to leave the marina for a day sail. Which check do you run before starting the engine?",
      "Engine started but no water out of the exhaust after a minute. Which WOBBLE letter did you skip?",
      "Strange whining noise from the engine bay and the battery isn't holding charge. Which letter would have caught it?",
    ],
    canonical: true,
  },

  {
    id: "five-essentials",
    acronym: "5 Essentials",
    name: "Five Essentials of sail trim",
    domain: "sailing",
    summary:
      "Course made good · Sail trim · Sail balance · Boat balance · Centreboard / daggerboard.",
    whenToUse:
      "Continuously, while sailing — when the boat doesn't feel right, walk down the five and see which one's off.",
    steps: [
      {
        key: "1",
        label: "Course made good",
        detail:
          "Are you still pointing where you want to go? Pick a transit or distant feature to steer on; check every minute or so.",
      },
      {
        key: "2",
        label: "Sail trim",
        detail:
          "Telltales: both windward and leeward streaming horizontally = trimmed. Windward lifts = pinching, bear off OR sheet in. Leeward lifts = over-eased, sheet in OR head up.",
      },
      {
        key: "3",
        label: "Sail balance",
        detail:
          "Jib and main working together. Too much main → weather helm (boat rounds up). Too much jib → lee helm (boat falls off). Tune by easing one or trimming the other.",
      },
      {
        key: "4",
        label: "Boat balance",
        detail:
          "Crew weight fore-aft and side-to-side. Flat is fast in most dinghies; slight leeward heel in very light wind helps the sails set. In gusts, hike out hard.",
      },
      {
        key: "5",
        label: "Centreboard / daggerboard",
        detail:
          "Fully down close-hauled, half up beam reach, quarter up broad reach, fully up downwind. Less board = less drag downwind; more board = more lift upwind.",
      },
    ],
    rule: null,
    example:
      "Boat keeps rounding up into the wind — start at #3: sail balance. Probably too much main; ease the main sheet, jib stays trimmed in.",
    courses: ["dinghy-l1", "dinghy-l2", "dinghy-l3", "competent-crew"],
    scenarios: [
      "The boat keeps trying to round up into the wind. Which checklist do you run?",
      "You're sailing close-hauled but the jib telltales keep lifting on the windward side. Which 'Essential' is off?",
      "Reaching downwind in a dinghy, the boat feels sluggish and the bow plunges. Which 'Essential' would help?",
    ],
    canonical: true,
  },

  {
    id: "rule-of-twelfths",
    acronym: "Rule of Twelfths",
    name: "Tide height between HW and LW",
    domain: "tides",
    summary:
      "Tide rises 1/12 in hour 1, 2/12 in hour 2, 3/12 in hour 3, 3/12 in hour 4, 2/12 in hour 5, 1/12 in hour 6.",
    whenToUse:
      "Estimating the height of tide at any time between HW and LW, without an Admiralty tidal curve. Assumes the tide is a symmetrical sinusoid over six hours (close enough for the Atlantic; less accurate where double tides apply).",
    steps: [
      { key: "h1", label: "1/12 of the range", detail: "First hour after HW (or LW): tide moves 1/12 of the total range." },
      { key: "h2", label: "2/12 (1/6)", detail: "Second hour: 2/12 — together with h1 = 3/12 of the range." },
      { key: "h3", label: "3/12 (1/4)", detail: "Third hour: 3/12 — together with h1+h2 = 6/12 = half the range." },
      { key: "h4", label: "3/12 (1/4)", detail: "Fourth hour: 3/12 — together = 9/12 of the range." },
      { key: "h5", label: "2/12 (1/6)", detail: "Fifth hour: 2/12 — together = 11/12 of the range." },
      { key: "h6", label: "1/12", detail: "Sixth hour: 1/12 — together = full range, you've reached the other end." },
    ],
    rule:
      "Height now = HW height − (twelfths-elapsed / 12) × range  [falling tide]\nHeight now = LW height + (twelfths-elapsed / 12) × range  [rising tide]",
    example:
      "HW 5.2 m, LW 1.6 m → range 3.6 m. At HW+3h on a falling tide: 6/12 elapsed = 1.8 m fall. Height now = 5.2 − 1.8 = 3.4 m.",
    courses: ["day-skipper", "coastal-skipper", "yachtmaster-offshore"],
    scenarios: [
      "HW Dover at 0900, height 6.0 m; LW 1500, height 1.2 m. What's the depth of tide at 1200?",
      "You want to leave with 2 m clearance over a 0.5 m drying patch; the secondary port has HW 4.4 m and LW 0.8 m at HW 1100. Latest time you can stay?",
      "Working a tidal gate: HW 0530, LW 1145, range 4.8 m. How much tide at 0800?",
    ],
    canonical: true,
  },

  {
    id: "buys-ballot",
    acronym: "Buys Ballot",
    name: "Locating a low pressure from wind direction",
    domain: "weather",
    summary:
      "Northern hemisphere: stand with your back to the wind — the low-pressure centre is to your LEFT.",
    whenToUse:
      "At sea, with no synoptic chart but a steady wind: identify which way the depression is and which way it's heading. Particularly useful when a TRS may be approaching (Yachtmaster Ocean).",
    steps: [],
    rule:
      "Northern hemisphere: BACK to wind → LOW pressure on your LEFT, HIGH pressure on your right.\nSouthern hemisphere: BACK to wind → LOW on your RIGHT, HIGH on your left.\nFollows from the Coriolis force deflecting air to the right (N) / left (S) of pressure gradient — air spirals into a low anticlockwise (N) / clockwise (S).",
    example:
      "Northern hemisphere, wind out of the south-west: face north-east (your back to it) → low pressure to your left → north-west of you. If wind backs into the south, low has moved closer / overhead.",
    courses: ["day-skipper", "coastal-skipper", "yachtmaster-offshore", "yachtmaster-ocean"],
    scenarios: [
      "You're sailing the English Channel; wind is fresh from the south-west and the barometer's falling. Where's the depression centre, roughly?",
      "Mid-Atlantic, southern hemisphere, wind from the north-east and pressure dropping. Where's the low pressure?",
      "The wind backs from south-west to south. What does that tell you about the depression's position?",
    ],
    canonical: true,
  },

  {
    id: "irpcs-pecking-order",
    acronym: "NUC · RAM · CBD · F · S · P",
    name: "IRPCS give-way pecking order",
    domain: "irpcs",
    summary:
      "Each vessel type gives way to all those above it. Power gives way to almost everyone.",
    whenToUse:
      "Every encounter: identify both vessels' status, then the lower-ranked one gives way.",
    steps: [
      {
        key: "1",
        label: "NUC — Not Under Command",
        detail:
          "Vessel unable to manoeuvre — breakdown, lost rudder, etc. Day: two black balls vertical. Night: two red lights vertical. Highest priority.",
      },
      {
        key: "2",
        label: "RAM — Restricted in ability to Manoeuvre",
        detail:
          "Vessel whose work limits its manoeuvring — dredgers, divers, towing operations, mine-clearance. Day: ball-diamond-ball vertical. Night: red-white-red vertical.",
      },
      {
        key: "3",
        label: "CBD — Constrained By Draught",
        detail:
          "Deep-draught vessels in a channel where depth limits their options. Day: cylinder. Night: three red vertical lights in addition to normal sidelights.",
      },
      {
        key: "4",
        label: "F — Fishing",
        detail:
          "Vessel actively fishing with nets, trawls, lines (not lures). Day: two cones apex-to-apex (or a basket). Night: red-over-white (other than trawling) or green-over-white (trawling).",
      },
      {
        key: "5",
        label: "S — Sailing",
        detail:
          "Under sail, no engine. If the engine is on AND in gear, you are NOT a sailing vessel for the rules — you are a power-driven vessel and rank as P.",
      },
      {
        key: "6",
        label: "P — Power",
        detail:
          "Any vessel propelled by machinery — including a yacht motor-sailing. Gives way to all above. Within power-vs-power: head-on both turn to starboard; crossing the one with the other on her starboard gives way.",
      },
    ],
    rule: null,
    example:
      "You're under sail close-hauled. A trawler with nets out crosses your bow on a converging course. Fishing > Sail → YOU give way.",
    courses: ["competent-crew", "day-skipper", "coastal-skipper", "yachtmaster-offshore"],
    scenarios: [
      "Under sail you spot a fishing vessel with a basket on its mast crossing your bow. Who gives way?",
      "You're motoring at 5 kt; a yacht under sail is crossing your bow. Who gives way?",
      "A dredger displaying ball-diamond-ball is working ahead of you in a narrow channel. Who has priority?",
    ],
    canonical: true,
  },

  {
    id: "four-ps",
    acronym: "4 P's",
    name: "Passage-planning checklist",
    domain: "passage",
    summary:
      "Plan · People · Plot · Provisions — the four headings every RYA Day Skipper passage plan covers.",
    whenToUse:
      "Before every passage. The depth of each P scales with the passage: a half-day coastal hop fills each in 2 minutes; an offshore trip needs each as its own page.",
    steps: [
      {
        key: "P1",
        label: "Plan",
        detail:
          "Route + alternatives + tidal gates + ETA + contingency ports. Weather window + safe wind speeds + sea-state limits.",
      },
      {
        key: "P2",
        label: "People",
        detail:
          "Crew list + roles + experience + medication + emergency contact ashore. Crew briefing on safety equipment, harness policy, watch system.",
      },
      {
        key: "P3",
        label: "Plot",
        detail:
          "Chart + waypoints + bearings + clearing transits + danger bearings. GPS programmed + chart-plotter routes + paper chart up. Compass courses pre-computed including variation.",
      },
      {
        key: "P4",
        label: "Provisions",
        detail:
          "Fuel + water + food + galley gas. Spares + medical kit + flares + first-aid + battery state + holding-tank capacity.",
      },
    ],
    rule: null,
    example:
      "Solent to Cherbourg, 60 nm: Plan = leave HW Lymington -2, arrive Cherbourg HW -1; People = 4 crew, watch system 2-on 4-off; Plot = waypoints CH1-CH4, danger bearings off Hurst, Casquets clear; Provisions = 80% fuel, 100% water, 3 days food.",
    courses: ["day-skipper", "coastal-skipper", "yachtmaster-offshore", "yachtmaster-ocean"],
    scenarios: [
      "You're planning a 40-mile coastal passage with two crew. What four headings does the RYA passage plan cover?",
      "Half-way through a passage you realise the weather has deteriorated. Which of the 4 P's most needs reviewing?",
      "A new crew member joins for an offshore passage. Which P puts them at the centre?",
    ],
    canonical: false,
  },

  {
    id: "mayday-call",
    acronym: "MAYDAY",
    name: "Distress call sequence",
    domain: "distress",
    summary:
      "Standard VHF Mayday: triple Mayday, identify, position, distress nature, assistance, persons on board, other info.",
    whenToUse:
      "Grave + imminent danger to vessel or persons. For urgency without grave danger use PAN PAN (same structure, different prefix). Press DSC distress button FIRST if fitted, then voice on Ch 16.",
    steps: [
      { key: "1", label: "MAYDAY × 3", detail: "Three times — alerts every receiver on Ch 16 + activates audio gating." },
      { key: "2", label: "\"This is\" + vessel name × 3", detail: "Identify clearly so SAR can address you by name." },
      { key: "3", label: "MAYDAY + vessel name", detail: "Single Mayday plus your name — locks the channel for your traffic." },
      { key: "4", label: "Position", detail: "Latitude / longitude or bearing + range from a known point. Read off the GPS." },
      { key: "5", label: "Nature of distress", detail: "What's wrong — sinking, fire, MOB, abandoning ship, structural failure." },
      { key: "6", label: "Assistance required", detail: "What you need — evacuation, helo lift, towing, medical advice." },
      { key: "7", label: "Persons on board", detail: "Total number of people including children — drives SAR resource allocation." },
      { key: "8", label: "Any other info", detail: "Type of vessel, colour, flares, EPIRB activated, abandoning to liferaft etc." },
      { key: "9", label: "\"Over\"", detail: "Release the PTT and listen for the Coastguard's response." },
    ],
    rule: null,
    example:
      "MAYDAY MAYDAY MAYDAY · This is yacht Aurora, yacht Aurora, yacht Aurora · MAYDAY yacht Aurora · Position 50° 30.2'N 002° 15.8'W · We are sinking after a collision · We require immediate assistance · Three persons on board · Liferaft launched · OVER.",
    courses: ["day-skipper", "coastal-skipper", "yachtmaster-offshore", "yachtmaster-ocean"],
    scenarios: [
      "Engine room fire and the extinguisher hasn't held it. What VHF call do you make?",
      "Crew member has fallen overboard and you can't recover them alone. What VHF call?",
      "The skipper has collapsed with chest pain mid-Channel. What VHF call?",
    ],
    canonical: true,
  },
]);

export const MNEMONIC_IDS: readonly string[] = MNEMONICS.map((m) => m.id);

/// Lookup helper used by the Reference panel and the drill.
export function findMnemonic(id: string): Mnemonic | undefined {
  return MNEMONICS.find((m) => m.id === id);
}

/// Filter mnemonics by their domain — used to embed each mnemonic
/// in its native module Reference section without duplicating data.
export function mnemonicsByDomain(d: MnemonicDomain): readonly Mnemonic[] {
  return MNEMONICS.filter((m) => m.domain === d);
}
